// The dashboard's chart components, presentational: data in through props,
// no APIs. Every chart has a text equivalent (legend values, titles on
// marks), status colours carry a label, and figures use tabular numerals.
import * as React from 'react';
import { type Color, type GameFilter, type Phase } from '@chess/api-model';
import { withStage } from '@chess/api-games';
import { formatFilter } from '@chess/api-games/filter';
import { Skeleton } from '@chess/api-skeleton';
import type { OpeningRow, RatingSeries, ResultCounts } from './types.ts';
import { slicePath } from './pie.ts';
import { type TimeClass } from '@chess/api-model';
import { MOVE_GRADES, MOVE_GRADE_LABEL, type MoveGrade } from '@chess/api-review';

/**
 * How a chart opens the library on a slice: the address of the library
 * filtered to it, and the in-app navigation there. Absent in stories that
 * show no links, and on cards with nothing to filter by.
 */
export interface FilterLink {
  href(filter: GameFilter): string;
  open(filter: GameFilter): void;
}

/**
 * The cards' and the openings screen's link into the library filtered to a
 * slice. The library, like the cards, covers the last 30 days only, so it
 * lists the games the card counted. `go` navigates in the app (the router's
 * `push`). A stage card passes its `stage`: the library then opens its games'
 * reviews on that stage (`?stage=`, 2026-10-13).
 */
export function libraryLink(go: (path: string) => void, stage?: Phase): FilterLink {
  const path = (filter: GameFilter) => withStage(`/library${formatFilter(filter)}`, stage);
  return { href: path, open: (filter) => go(path(filter)) };
}

/** A link to the filtered library, or plain text without one. */
export const FilterAnchor: React.FC<{ link?: FilterLink; filter?: GameFilter; className?: string; title?: string; style?: React.CSSProperties; children?: React.ReactNode }> = ({
  link,
  filter,
  className,
  title,
  style,
  children,
}) =>
  link && filter ? (
    <a
      className={`dash-link${className ? ` ${className}` : ''}`}
      href={link.href(filter)}
      title={title}
      style={style}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        link.open(filter);
      }}>
      {children}
    </a>
  ) : (
    <span className={className} title={title} style={style}>
      {children}
    </span>
  );

const RESULT_LABEL = { win: 'Wins', draw: 'Draws', loss: 'Losses' } as const;
const RESULT_KEYS = ['win', 'draw', 'loss'] as const;
export const TIME_CLASS_LABEL: Record<TimeClass, string> = { bullet: 'Bullet', blitz: 'Blitz', rapid: 'Rapid', daily: 'Daily' };

const total = (r: ResultCounts) => r.win + r.draw + r.loss;
const pct = (n: number, of: number) => (of ? `${Math.round((100 * n) / of)}%` : '0%');
export const signed = (pawns: number) => `${pawns > 0 ? '+' : pawns < 0 ? '−' : ''}${Math.abs(pawns).toFixed(2)}`;

/** A hero number with its unit; while `loading`, a skeleton of the number's size stands in for it and the unit stays. */
export const StatTile: React.FC<{ value?: number; unit: string; loading?: boolean }> = ({ value, unit, loading = false }) => (
  <div className="stat">
    <span className="stat-value">
      <Skeleton loading={loading} inline variant="text" shape="188">
        {value}
      </Skeleton>
    </span>
    <span className="stat-delta">{unit}</span>
  </div>
);

/**
 * A dashboard card's props: its data, or `loading` while the game store is
 * still reading the games. A loading card keeps its title and every label
 * that does not come from the data, and shows a skeleton for each value.
 */
export type CardProps<Data, Extra = {}> = Extra & (({ loading?: false } & Data) | ({ loading: true } & { [K in keyof Data]?: undefined }));

/** A text value still loading: a skeleton the size of `sample`, inline. */
export const TextSkeleton: React.FC<{ sample: string }> = ({ sample }) => <Skeleton loading inline variant="text" shape={sample} />;

/** A block (a bar, a chart's area) still loading: a skeleton of the given size, filling its box by default. */
export const BlockSkeleton: React.FC<{ width?: string; height?: string }> = ({ width = '100%', height = '100%' }) => (
  <Skeleton loading variant="text" width={width} height={height} />
);

const BAR_SHARE = [80, 55, 35, 65, 45, 25];

/**
 * Stand-in rows for a card whose data is loading, on the bar rows' grid: each
 * row keeps its label when the label does not come from the data (stages,
 * colours, pieces), or a text skeleton when it does (`undefined`: opening
 * names, weeks); its bar and its value are skeletons.
 */
export const SkeletonRows: React.FC<{ labels: Array<string | undefined>; value?: string; label?: string }> = ({ labels, value = '0–0–0', label = 'Sicilian Defense' }) => (
  <div className="split-rows">
    {labels.map((text, i) => (
      <div className="split-row" key={i}>
        <span className="split-label">{text ?? <TextSkeleton sample={label} />}</span>
        <span className="split-bar" style={{ width: `${BAR_SHARE[i % BAR_SHARE.length]}%` }}>
          <BlockSkeleton height="0.625rem" />
        </span>
        <span className="split-total">
          <TextSkeleton sample={value} />
        </span>
      </div>
    ))}
  </div>
);

/**
 * A chart still loading: a skeleton block with the chart's proportions (its
 * viewBox), or, with `fill`, filling the space a filling chart takes
 * (`.plot-fill`).
 */
export const SkeletonPlot: React.FC<{ width?: number; height?: number; label?: string; fill?: boolean }> = ({ width = 320, height = 140, label, fill = false }) => (
  <div className={fill ? 'skeleton-plot plot-fill' : 'skeleton-plot'} style={fill ? undefined : { aspectRatio: `${width} / ${height}` }}>
    <Skeleton loading variant="rounded" width="100%" height="100%" label={label} />
  </div>
);

/**
 * The pixel size of an element, kept current as it resizes; undefined until
 * first measured (and where ResizeObserver is missing). A chart drawn at
 * this size, with a viewBox of the same numbers, fills its box without
 * stretching its text or strokes.
 */
export function useElementSize<T extends Element>(): [React.RefObject<T>, { width: number; height: number } | undefined] {
  const ref = React.useRef<T>(null);
  const [size, setSize] = React.useState<{ width: number; height: number }>();
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const read = () => {
      const r = el.getBoundingClientRect();
      const width = Math.round(r.width);
      const height = Math.round(r.height);
      setSize((old) => (old && old.width === width && old.height === height ? old : width && height ? { width, height } : old));
    };
    read();
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, size];
}

const NO_RESULTS: ResultCounts = { win: 0, draw: 0, loss: 0 };

/**
 * Wins, draws and losses as a donut with a labelled legend. While `loading`,
 * the donut is a circular skeleton and each legend value a text skeleton;
 * the labels and swatches stay, so the card keeps its shape.
 */
export const ResultDonut: React.FC<{ results?: ResultCounts; size?: number; link?: FilterLink; loading?: boolean }> = ({ results = NO_RESULTS, size = 96, link, loading = false }) => {
  if (loading) {
    return (
      <div className="donut-wrap">
        <Skeleton loading variant="circular" width={`${size / 16}rem`} height={`${size / 16}rem`} label="Loading the results" />
        <ul className="legend">
          {RESULT_KEYS.map((k) => (
            <li key={k}>
              <span className="legend-swatch" style={{ background: `var(--viz-${k})` }} />
              {RESULT_LABEL[k]}
              <span className="legend-value">
                <Skeleton loading inline variant="text" shape="12 · 34%" />
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  const sum = total(results);
  const r = size / 2 - 8;
  const c = 2 * Math.PI * r;
  let offset = 0;
  const gap = sum > 1 ? 2 : 0;
  return (
    <div className="donut-wrap">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={RESULT_KEYS.map((k) => `${results[k]} ${RESULT_LABEL[k].toLowerCase()}`).join(', ')}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--viz-grid)" strokeWidth={14} />
        {sum > 0 &&
          RESULT_KEYS.map((k) => {
            const len = (results[k] / sum) * c;
            const seg = (
              <circle
                key={k}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={`var(--viz-${k})`}
                strokeWidth={14}
                strokeDasharray={`${Math.max(0, len - gap)} ${c}`}
                strokeDashoffset={-offset}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
                style={link ? { cursor: 'pointer' } : undefined}
                onClick={link ? () => link.open({ outcome: k }) : undefined}>
                <title>{`${RESULT_LABEL[k]}: ${results[k]} (${pct(results[k], sum)})`}</title>
              </circle>
            );
            offset += len;
            return results[k] > 0 ? seg : null;
          })}
      </svg>
      <ul className="legend">
        {RESULT_KEYS.map((k) => (
          <li key={k}>
            <span className="legend-swatch" style={{ background: `var(--viz-${k})` }} />
            <FilterAnchor link={results[k] ? link : undefined} filter={{ outcome: k }} title={`Open the library: ${RESULT_LABEL[k].toLowerCase()}`}>
              {RESULT_LABEL[k]}
            </FilterAnchor>
            <span className="legend-value">
              {results[k]} · {pct(results[k], sum)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** One slice of a `Pie`: its count, colour token, the tooltip and, when it has one, the filter that lists its games. */
export interface PieSlice {
  key: string;
  label: string;
  count: number;
  /** A colour token, e.g. `var(--viz-win)`. */
  colour: string;
  /** The slice's tooltip after its label, count and share. */
  title?: string;
  filter?: GameFilter;
}

/**
 * A pie of slices in the order given, from 12 o'clock, a thin gap in the
 * card's colour between slices. A slice with a filter opens the library on
 * it (`link`). The legend is the caller's (`PieLegend`, or a domain legend).
 */
export const Pie: React.FC<{ slices: PieSlice[]; size?: number; link?: FilterLink; label?: string }> = ({ slices, size = 96, link, label }) => {
  const sum = slices.reduce((s, x) => s + x.count, 0);
  const r = size / 2 - 2;
  let at = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label ?? slices.map((s) => `${s.count} ${s.label.toLowerCase()}`).join(', ')}>
      {slices.map((s) => {
        if (!s.count || !sum) return null;
        const from = at;
        at += s.count / sum;
        const open = link && s.filter ? () => link.open(s.filter as GameFilter) : undefined;
        return (
          <path
            key={s.key}
            d={slicePath(from, at, r, size / 2)}
            fill={s.colour}
            stroke="var(--color-surface)"
            strokeWidth={sum > s.count ? 2 : 0}
            strokeLinejoin="round"
            style={open ? { cursor: 'pointer' } : undefined}
            onClick={open}>
            <title>{`${s.label}: ${s.count} (${pct(s.count, sum)})${s.title ? ` · ${s.title}` : ''}`}</title>
          </path>
        );
      })}
    </svg>
  );
};

/** A pie's legend: each slice's swatch, its label (a library link when it has a filter and games) and count with share. */
export const PieLegend: React.FC<{ slices: PieSlice[]; link?: FilterLink }> = ({ slices, link }) => {
  const sum = slices.reduce((s, x) => s + x.count, 0);
  return (
    <ul className="legend">
      {slices.map((s) => (
        <li key={s.key}>
          <span className="legend-swatch" style={{ background: s.colour }} />
          <FilterAnchor link={s.filter && s.count ? link : undefined} filter={s.filter} title={s.title}>
            {s.label}
          </FilterAnchor>
          <span className="legend-value">
            {s.count} · {pct(s.count, sum)}
          </span>
        </li>
      ))}
    </ul>
  );
};

export interface SplitRow {
  key: string;
  label: React.ReactNode;
  title?: string;
  results: ResultCounts;
  extra?: React.ReactNode;
  /** The library filter for the row; each segment adds its result. */
  filter?: GameFilter;
}

/** One stacked win/draw/loss bar per row, scaled to the largest row, with the total. */
export const ResultSplitRows: React.FC<{ rows: SplitRow[]; legend?: boolean; link?: FilterLink }> = ({ rows, legend = true, link }) => {
  const max = Math.max(1, ...rows.map((r) => total(r.results)));
  return (
    <>
      <div className="split-rows">
        {rows.map((row) => {
          const sum = total(row.results);
          return (
            <div className="split-row" key={row.key} title={row.title}>
              <FilterAnchor className="split-label" link={sum ? link : undefined} filter={row.filter}>
                {row.label}
              </FilterAnchor>
              <span className="split-bar" style={{ width: `${(100 * sum) / max}%` }}>
                {RESULT_KEYS.map((k) =>
                  row.results[k] > 0 ? (
                    <FilterAnchor
                      key={k}
                      className="split-segment"
                      link={link}
                      filter={row.filter && { ...row.filter, outcome: k }}
                      style={{ flex: row.results[k], background: `var(--viz-${k})` }}
                      title={`${RESULT_LABEL[k]}: ${row.results[k]} of ${sum}`}
                    />
                  ) : null,
                )}
              </span>
              <span className="split-total">
                {row.extra ?? `${row.results.win}–${row.results.draw}–${row.results.loss}`}
              </span>
            </div>
          );
        })}
      </div>
      {legend && <ResultLegend />}
    </>
  );
};

/** A move grade's short name in a legend: "inaccuracy", "blunder". */
export const gradeName = (grade: MoveGrade) => (grade === 'best' || grade === 'good' ? grade : MOVE_GRADE_LABEL[grade].toLowerCase());

/** The key to the move grades (`--viz-grade-*`), best to blunder, then miss; only `grades` when given. */
export const MoveGradeLegend: React.FC<{ grades?: readonly MoveGrade[] }> = ({ grades = MOVE_GRADES }) => (
  <ul className="legend-row">
    {grades.map((g) => (
      <li key={g}>
        <span className={`legend-swatch grade-fill-${g}`} aria-hidden="true" />
        {gradeName(g)}
      </li>
    ))}
  </ul>
);

export const ResultLegend: React.FC = () => (
  <ul className="legend-row" aria-label="Legend">
    {RESULT_KEYS.map((k) => (
      <li key={k}>
        <span className="legend-swatch" style={{ background: `var(--viz-${k})` }} />
        {RESULT_LABEL[k]}
      </li>
    ))}
  </ul>
);

/** A small piece glyph for a colour, labelled. */
export const ColourMark: React.FC<{ colour: Color }> = ({ colour }) => (
  <span className={`colour-mark colour-mark-${colour}`} role="img" aria-label={colour === 'white' ? 'as White' : 'as Black'} title={colour === 'white' ? 'as White' : 'as Black'} />
);

const dateLabel = (at: number) => new Date(at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });

/**
 * One line per time class over the period, a legend naming each; a class
 * with one game is a point. With `fill`, the plot grows to the space its
 * container leaves (the card's height, less its head and the legend; at
 * least `.plot-fill`'s minimum, at most the dashboard's chart cap), drawn at
 * its measured pixel size.
 */
export const RatingLines: React.FC<{ series: RatingSeries[]; from: number; to: number; width?: number; height?: number; link?: FilterLink; fill?: boolean }> = ({
  fill = false,
  ...props
}) => {
  const [box, size] = useElementSize<HTMLDivElement>();
  if (!fill) return <RatingPlot {...props} />;
  return (
    <div className="lines lines-fill">
      <div className="plot-fill" ref={box}>
        {size && <RatingPlot {...props} width={size.width} height={size.height} legend={false} />}
      </div>
      <RatingLegend series={props.series} link={props.link} />
    </div>
  );
};

const RatingLegend: React.FC<{ series: RatingSeries[]; link?: FilterLink }> = ({ series, link }) => (
  <ul className="legend-row" aria-label="Legend">
    {series.map((s) => (
      <li key={s.timeClass}>
        <span className="legend-swatch" style={{ background: `var(--viz-${s.timeClass})` }} />
        <FilterAnchor link={link} filter={{ timeClass: s.timeClass }} title={`Open the library: ${TIME_CLASS_LABEL[s.timeClass].toLowerCase()} games`}>
          {TIME_CLASS_LABEL[s.timeClass]}
        </FilterAnchor>{' '}
        {s.points[s.points.length - 1].rating}
      </li>
    ))}
  </ul>
);

const RatingPlot: React.FC<{ series: RatingSeries[]; from: number; to: number; width?: number; height?: number; link?: FilterLink; legend?: boolean }> = ({
  series,
  from,
  to,
  width = 320,
  height = 140,
  link,
  legend = true,
}) => {
  const pad = { left: 34, right: 8, top: 8, bottom: 18 };
  const ratings = series.reduce<number[]>((all, s) => all.concat(s.points.map((p) => p.rating)), []);
  const span = Math.max(...ratings) - Math.min(...ratings);
  const step = span > 300 ? 100 : span > 120 ? 50 : 25;
  const lo = Math.floor((Math.min(...ratings) - 5) / step) * step;
  const hi = Math.ceil((Math.max(...ratings) + 5) / step) * step;
  const x = (at: number) => pad.left + ((at - from) / Math.max(1, to - from)) * (width - pad.left - pad.right);
  const y = (r: number) => pad.top + (1 - (r - lo) / Math.max(1, hi - lo)) * (height - pad.top - pad.bottom);
  const ticks: number[] = [];
  for (let t = lo; t <= hi; t += step * Math.max(1, Math.round((hi - lo) / step / 3))) ticks.push(t);
  return (
    <div className="lines">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={series.map((s) => `${TIME_CLASS_LABEL[s.timeClass]}: ${s.points.map((p) => p.rating).join(', ')}`).join('; ')}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.left} x2={width - pad.right} y1={y(t)} y2={y(t)} stroke="var(--viz-grid)" strokeWidth={1} />
            <text x={pad.left - 4} y={y(t) + 3} textAnchor="end">
              {t}
            </text>
          </g>
        ))}
        <text x={pad.left} y={height - 4}>
          {dateLabel(from)}
        </text>
        <text x={width - pad.right} y={height - 4} textAnchor="end">
          {dateLabel(to)}
        </text>
        {series.map((s) => {
          const colour = `var(--viz-${s.timeClass})`;
          const d = s.points.map((p, i) => `${i ? 'L' : 'M'}${x(p.at).toFixed(1)},${y(p.rating).toFixed(1)}`).join(' ');
          return (
            <g key={s.timeClass}>
              {s.points.length > 1 && <path d={d} fill="none" stroke={colour} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />}
              {s.points.map((p, i) => (
                <g key={i}>
                  <title>{`${TIME_CLASS_LABEL[s.timeClass]} · ${dateLabel(p.at)} · ${p.rating}`}</title>
                  <circle className="chart-hit" cx={x(p.at)} cy={y(p.rating)} r={8} />
                  <circle
                    className={i === s.points.length - 1 ? 'chart-dot' : 'chart-dot chart-dot-hidden'}
                    cx={x(p.at)}
                    cy={y(p.rating)}
                    r={4}
                    fill={colour}
                    stroke="var(--color-surface)"
                    strokeWidth={2}
                  />
                </g>
              ))}
            </g>
          );
        })}
      </svg>
      {legend && <RatingLegend series={series} link={link} />}
    </div>
  );
};

export interface DivergingRow {
  key: string;
  label: string;
  value?: number;
  detail?: string;
}

/** Horizontal bars around zero, good to the right in green, bad to the left in red, each labelled with its value. */
export const DivergingBars: React.FC<{ rows: DivergingRow[]; width?: number }> = ({ rows, width = 320 }) => {
  const rowH = 22;
  const labelW = 86;
  const valueW = 46;
  const plotW = width - labelW - valueW;
  const max = Math.max(0.5, ...rows.map((r) => Math.abs(r.value ?? 0)));
  const mid = labelW + plotW / 2;
  const height = rows.length * rowH;
  return (
    <div className="diverging">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={rows.map((r) => `${r.label}: ${r.value === undefined ? 'no data' : signed(r.value)}`).join(', ')}>
        <line x1={mid} x2={mid} y1={0} y2={height} stroke="var(--viz-grid)" strokeWidth={1} />
        {rows.map((r, i) => {
          const yy = i * rowH + 5;
          const w = r.value === undefined ? 0 : (Math.abs(r.value) / max) * (plotW / 2);
          const good = (r.value ?? 0) >= 0;
          return (
            <g key={r.key}>
              <text x={0} y={yy + 9} style={{ fill: 'var(--viz-ink)' }}>
                {r.label}
              </text>
              {r.value !== undefined && (
                <rect x={good ? mid : mid - w} y={yy} width={Math.max(2, w)} height={12} rx={2} fill={good ? 'var(--viz-win)' : 'var(--viz-loss)'}>
                  <title>{`${r.label}: ${signed(r.value)}${r.detail ? ` · ${r.detail}` : ''}`}</title>
                </rect>
              )}
              <text x={width} y={yy + 9} textAnchor="end" style={{ fill: 'var(--viz-ink)' }}>
                {r.value === undefined ? '—' : signed(r.value)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

/** Plain magnitude bars in one neutral colour, labelled with their counts. */
/** One row of a "Moves to practise" card: a kind of move, the games with it, and the library filter that lists them. */
export interface PractiseRow {
  key: string;
  label: string;
  /** Games with at least one such move. */
  count: number;
  /** A CSS colour for the bar (the losses' red ramp). */
  colour: string;
  /** The tooltip. */
  title?: string;
  filter: GameFilter;
}

/**
 * The rows of a "Moves to practise" card (2026-10-08; the Middlegame and
 * Ending sections' second cards): each a kind of move to practise, its label
 * a link to the library filtered to the games with it, a bar scaled to the
 * largest row, the games at the right. A row with no games is not a link.
 */
export const PractiseRows: React.FC<{ rows: PractiseRow[]; link?: FilterLink }> = ({ rows, link }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="practise-rows">
      {rows.map((r) => {
        const label =
          link && r.count ? (
            <a
              className="practise-label"
              href={link.href(r.filter)}
              onClick={(e) => {
                if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                e.preventDefault();
                link.open(r.filter);
              }}>
              {r.label} ›
            </a>
          ) : (
            <span className="practise-label practise-none">{r.label}</span>
          );
        return (
          <div key={r.key} className="practise-row" title={r.title}>
            {label}
            <span className="practise-bar">
              <span className="practise-fill" style={{ width: `${(100 * r.count) / max}%`, background: r.colour }} />
            </span>
            <span className="practise-count">{r.count}</span>
          </div>
        );
      })}
    </div>
  );
};

export const CountBars: React.FC<{ rows: Array<{ key: string; label: string; count: number; title?: string; filter?: GameFilter }>; link?: FilterLink }> = ({ rows, link }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="split-rows">
      {rows.map((r) => (
        <div className="split-row" key={r.key} title={r.title}>
          <FilterAnchor className="split-label" link={r.count ? link : undefined} filter={r.filter}>
            {r.label}
          </FilterAnchor>
          <span className="split-bar" style={{ width: `${(100 * r.count) / max}%` }}>
            {r.count > 0 && <FilterAnchor className="split-segment" link={link} filter={r.filter} style={{ flex: 1, background: 'var(--viz-loss)' }} />}
          </span>
          <span className="split-total">{r.count}</span>
        </div>
      ))}
    </div>
  );
};

/**
 * Openings as win/draw/loss bars, each named with the reader's colour, its
 * record, and the average score at the end of the opening over its scanned
 * games ("…" while none is scanned).
 */
/**
 * One row per opening: its name with the reader's colour, a bar from zero
 * scaled to the largest score in the list, and the average score after the
 * opening. Green above zero, red below.
 */
/**
 * Openings with the average score after the opening, a bar from zero. With
 * `games`, each row also shows how many games it holds, and a row with no
 * scanned game shows a dash instead of a score.
 */
export const OpeningScoreRows: React.FC<{ rows: OpeningRow[]; link?: FilterLink; games?: boolean }> = ({ rows, link, games }) => {
  const max = Math.max(0.5, ...rows.map((r) => Math.abs(r.averageScore ?? 0)));
  return (
    <div className={`split-rows${games ? ' opening-score-rows-games' : ''}`}>
      {rows.map((r) => {
        const score = r.averageScore;
        const as = r.colour === 'white' ? 'White' : 'Black';
        const count = `${r.games} ${r.games === 1 ? 'game' : 'games'}`;
        const title =
          score === undefined
            ? `${r.opening} as ${as}: ${count}, none scanned yet`
            : `${r.opening} as ${as}: ${signed(score)} after the opening on average, over ${r.scanned} scanned of ${count}`;
        return (
          <div className="split-row opening-score-row" key={`${r.opening}|${r.colour}`} title={title}>
            <FilterAnchor className="split-label" link={link} filter={{ opening: r.opening, colour: r.colour }}>
              <ColourMark colour={r.colour} />
              {r.opening}
            </FilterAnchor>
            {games && <span className="opening-games">{r.games}</span>}
            <span className="opening-score-track">
              {score !== undefined && (
                <span className="opening-score-bar" style={{ width: `${(100 * Math.abs(score)) / max}%`, background: `var(--viz-${score >= 0 ? 'win' : 'loss'})` }} />
              )}
            </span>
            <span className="opening-score">{score === undefined ? <span className="dash-muted">—</span> : signed(score)}</span>
          </div>
        );
      })}
    </div>
  );
};

